import React, { useState, useRef, useEffect } from 'react';
import { Upload, Eye, EyeOff, X, Link2, Unlink, History, ChevronRight } from 'lucide-react';
import { IconBrandGoogle } from '@tabler/icons-react';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { compressProfileImage, PROFILE_IMAGE_ACCEPT } from '../../utils/compressProfileImage';
import { fetchPassPolicy, AuthHistoryModal } from '../security';
import { validatePasswordForPolicy } from '../security/passwordPolicyValidation';
import { setPreferGoogleLogin, clearPreferGoogleLogin } from '../../utils/googleLoginPreference';
import './UserProfileModal.css';

const API_BASE_URL = getApiBaseUrl();
const GOOGLE_AUTH_API = `${API_BASE_URL}/api/auth/google`;

const GOOGLE_LINK_ERRORS = {
  GOOGLE_ALREADY_LINKED: 'Esta conta Google já está vinculada a outro usuário.',
  not_configured: 'OAuth Google não configurado no servidor.',
  tenant: 'Login Google desabilitado para este cliente.',
  callback: 'Não foi possível vincular a conta Google.',
};

const revokeBlobPreviewUrl = (url) => {
  if (url && String(url).startsWith('blob:')) {
    try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
  }
};

const getClientIdFromLocalSession = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    if (userData?.clientId != null && userData.clientId !== '') return userData.clientId;
  } catch (_) { /* ignore */ }
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    const part = token.split('.')[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return json.clientId ?? null;
  } catch (_) { /* ignore */ }
  return null;
};

const getUserTypeLabel = (type) => {
  const t = String(type ?? '');
  if (t === '1') return 'Administrador';
  if (t === '2') return 'Gerente Técnico';
  if (t === '3') return 'Técnico';
  if (t === '4') return 'Financeiro';
  if (t === '0') return 'Usuário';
  return 'Cargo não informado';
};

const getInitials = (name) => {
  const text = String(name || '').trim();
  if (!text) return '?';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

function UserProfileModal({ isOpen, onClose, currentUser, onSaved }) {
  const [displayName, setDisplayName] = useState('');
  const [originalDisplayName, setOriginalDisplayName] = useState('');
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [photoPendingKey, setPhotoPendingKey] = useState(null);
  const [originalPhotoKey, setOriginalPhotoKey] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [passwordSectionOpen, setPasswordSectionOpen] = useState(false);
  const [showAuthHistory, setShowAuthHistory] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [passPolicy, setPassPolicy] = useState(null);
  const [passwordValidationRules, setPasswordValidationRules] = useState([]);
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [googleBusy, setGoogleBusy] = useState(false);

  const photoInputRef = useRef(null);
  const userId = currentUser?.id;

  const email = currentUser?.user || '';
  const roleLabel = getUserTypeLabel(currentUser?.type);
  const initials = getInitials(displayName || currentUser?.username);

  useEffect(() => {
    if (!isOpen) return;
    const name = String(currentUser?.username || currentUser?.name || '').trim();
    setDisplayName(name);
    setOriginalDisplayName(name);
    setPhotoPreviewUrl(currentUser?.profilePhotoUrl || null);
    setPhotoPendingKey(null);
    setOriginalPhotoKey(null);
    setPasswordSectionOpen(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setErrors({});
    setSubmitError('');
    setSuccessMessage('');
    setGoogleStatus(null);
    setGoogleBusy(false);
    fetchPassPolicy()
      .then((p) => {
        setPassPolicy(p);
        setPasswordValidationRules(validatePasswordForPolicy('', p).rules);
      })
      .catch(() => { setPassPolicy(null); setPasswordValidationRules([]); });

    const loadPhoto = async () => {
      if (!userId) return;
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/users/${userId}/profile-photo/url`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        const data = await res.json();
        if (data.success && data.viewUrl) {
          setPhotoPreviewUrl(data.viewUrl);
          const marker = data.hasPhoto !== false ? '__stored__' : null;
          setOriginalPhotoKey(marker);
          setPhotoPendingKey(marker);
        }
      } catch (_) { /* optional */ }
    };
    loadPhoto();

    const loadGoogle = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${GOOGLE_AUTH_API}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setGoogleStatus(data.data || null);
        } else {
          setGoogleStatus(null);
        }
      } catch (_) {
        setGoogleStatus(null);
      }
    };
    loadGoogle();
  }, [isOpen, userId, currentUser?.username, currentUser?.profilePhotoUrl]);

  const handleGoogleLink = async () => {
    if (googleBusy) return;
    setGoogleBusy(true);
    setSubmitError('');
    try {
      const token = localStorage.getItem('authToken');
      const origin = encodeURIComponent(window.location.origin);
      const res = await fetch(`${GOOGLE_AUTH_API}/link-url?origin=${origin}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success || !data.data?.url) {
        throw new Error(data.message || GOOGLE_LINK_ERRORS.callback);
      }
      const popup = window.open(data.data.url, 'pilot-google-oauth', 'width=520,height=640');
      if (!popup) {
        throw new Error('Permita pop-ups para vincular a conta Google.');
      }
      await new Promise((resolve) => {
        const onMsg = (event) => {
          if (event.origin !== window.location.origin) return;
          if (!event.data || event.data.type !== 'pilot-google-oauth') return;
          window.removeEventListener('message', onMsg);
          window.clearInterval(closedTimer);
          if (event.data.ok) {
            setPreferGoogleLogin();
            setSuccessMessage('Conta Google vinculada.');
            fetch(`${GOOGLE_AUTH_API}/status`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((r) => r.json())
              .then((d) => {
                if (d.success) setGoogleStatus(d.data);
              })
              .catch(() => {});
          } else {
            const code = event.data.error || 'callback';
            setSubmitError(GOOGLE_LINK_ERRORS[code] || GOOGLE_LINK_ERRORS.callback);
          }
          resolve();
        };
        window.addEventListener('message', onMsg);
        const closedTimer = window.setInterval(() => {
          if (popup.closed) {
            window.clearInterval(closedTimer);
            window.removeEventListener('message', onMsg);
            resolve();
          }
        }, 500);
      });
    } catch (err) {
      setSubmitError(err.message || GOOGLE_LINK_ERRORS.callback);
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleGoogleUnlink = async () => {
    if (googleBusy) return;
    if (!window.confirm('Desvincular a conta Google deste usuário?')) return;
    setGoogleBusy(true);
    setSubmitError('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${GOOGLE_AUTH_API}/link`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Falha ao desvincular');
      }
      setGoogleStatus((prev) => ({
        ...(prev || {}),
        linked: false,
        provider_email: null,
      }));
      clearPreferGoogleLogin();
      setSuccessMessage('Conta Google desvinculada.');
    } catch (err) {
      setSubmitError(err.message || 'Erro ao desvincular Google');
    } finally {
      setGoogleBusy(false);
    }
  };

  const handlePhotoSelect = async (event) => {
    const input = event.currentTarget;
    const file = input?.files?.[0];
    if (!file || !(file instanceof Blob)) return;
    if (input.files.length > 1) {
      input.value = '';
      alert('Envie apenas uma imagem por vez.');
      return;
    }
    const clientId = getClientIdFromLocalSession();
    if (!clientId) {
      input.value = '';
      alert('Não foi possível identificar o cliente da sessão. Faça login novamente.');
      return;
    }
    if (!userId) {
      input.value = '';
      return;
    }

    const previousPreview = photoPreviewUrl;
    const previousKey = photoPendingKey;
    const immediatePreview = URL.createObjectURL(file);
    revokeBlobPreviewUrl(photoPreviewUrl);
    setPhotoPreviewUrl(immediatePreview);
    setPhotoUploading(true);
    setSubmitError('');

    try {
      const compressed = await compressProfileImage(file);
      const token = localStorage.getItem('authToken');
      const presignRes = await fetch(`${API_BASE_URL}/api/users/profile-photo/presign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          mimeType: compressed.type,
          sizeBytes: compressed.size,
          fileName: compressed.name
        })
      });
      const presignData = await presignRes.json();
      if (!presignRes.ok || !presignData.success) {
        throw new Error(presignData.message || 'Falha ao preparar upload');
      }
      const uploadRes = await fetch(presignData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressed.type },
        body: compressed
      });
      if (!uploadRes.ok) throw new Error('Falha ao enviar imagem para o servidor');
      setPhotoPendingKey(presignData.key);
      const finalPreview = URL.createObjectURL(compressed);
      revokeBlobPreviewUrl(immediatePreview);
      setPhotoPreviewUrl(finalPreview);
    } catch (err) {
      revokeBlobPreviewUrl(immediatePreview);
      setPhotoPreviewUrl(previousPreview);
      setPhotoPendingKey(previousKey);
      setSubmitError(err.message || 'Erro ao enviar foto');
    } finally {
      input.value = '';
      setPhotoUploading(false);
    }
  };

  const wantsPasswordChange = passwordSectionOpen && (
    passwordForm.currentPassword.trim() ||
    passwordForm.newPassword.trim() ||
    passwordForm.confirmPassword.trim()
  );

  const nameChanged = displayName.trim() !== originalDisplayName;
  const photoChanged = Boolean(photoPendingKey && photoPendingKey !== originalPhotoKey);
  const isDirty = nameChanged || photoChanged || Boolean(wantsPasswordChange);

  const validate = () => {
    const next = {};
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      next.displayName = 'Nome e sobrenome é obrigatório';
    } else if (trimmedName.length > 20) {
      next.displayName = 'Máximo 20 caracteres';
    } else if (!/^[a-zA-Z0-9\s]+$/.test(trimmedName)) {
      next.displayName = 'Use apenas letras, números e espaços';
    }

    if (wantsPasswordChange) {
      if (!passwordForm.currentPassword) next.currentPassword = 'Senha atual é obrigatória';
      if (!passwordForm.newPassword) {
        next.newPassword = 'Nova senha é obrigatória';
      } else if (passPolicy) {
        const pwdVal = validatePasswordForPolicy(passwordForm.newPassword, passPolicy);
        if (!pwdVal.valid) next.newPassword = pwdVal.errors[0] || 'Senha não atende à política';
      }
      if (!passwordForm.confirmPassword) {
        next.confirmPassword = 'Confirme a nova senha';
      } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        next.confirmPassword = 'As senhas não coincidem';
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    setSubmitError('');
    setSuccessMessage('');
    if (!validate()) return;

    const trimmedName = displayName.trim();
    if (!isDirty) {
      setSubmitError('Nenhuma alteração para salvar');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('authToken');

      if (wantsPasswordChange) {
        const pwdRes = await fetch(`${API_BASE_URL}/api/users/change-password`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            currentPassword: passwordForm.currentPassword,
            newPassword: passwordForm.newPassword
          })
        });
        const pwdResult = await pwdRes.json();
        if (!pwdRes.ok) {
          if (pwdResult.message === 'Senha atual incorreta') {
            setErrors((e) => ({ ...e, currentPassword: 'Senha atual incorreta' }));
          } else {
            setSubmitError(pwdResult.message || 'Erro ao alterar senha');
          }
          setIsSaving(false);
          return;
        }
      }

      const profileBody = {};
      if (nameChanged) profileBody.username = trimmedName;
      if (photoPendingKey && photoPendingKey !== originalPhotoKey && photoPendingKey !== '__stored__') {
        profileBody.profile_photo_s3_key = photoPendingKey;
      }

      let profilePhotoUrl = photoPreviewUrl;
      if (Object.keys(profileBody).length > 0) {
        const profileRes = await fetch(`${API_BASE_URL}/api/users/me`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(profileBody)
        });
        const profileResult = await profileRes.json();
        if (!profileRes.ok || !profileResult.success) {
          setSubmitError(profileResult.message || 'Erro ao salvar perfil');
          setIsSaving(false);
          return;
        }
        if (profileResult.hasProfilePhoto) {
          try {
            const photoRes = await fetch(`${API_BASE_URL}/api/users/${userId}/profile-photo/url`, {
              headers: { Authorization: `Bearer ${token}` },
              credentials: 'include',
            });
            const photoData = await photoRes.json();
            if (photoData?.success && photoData.viewUrl) {
              profilePhotoUrl = photoData.viewUrl;
            }
          } catch (_) { /* keep preview */ }
        }
        setOriginalDisplayName(trimmedName);
        setOriginalPhotoKey(profileResult.hasProfilePhoto ? (photoPendingKey || '__stored__') : null);
      }

      const updates = {
        username: trimmedName,
        name: trimmedName,
        profilePhotoUrl: profilePhotoUrl || null
      };
      if (onSaved) onSaved(updates);

      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSectionOpen(false);
      setSuccessMessage(
        wantsPasswordChange && Object.keys(profileBody).length > 0
          ? 'Perfil e senha atualizados com sucesso!'
          : wantsPasswordChange
            ? 'Senha alterada com sucesso!'
            : 'Perfil atualizado com sucesso!'
      );
      setTimeout(() => {
        onClose();
        setSuccessMessage('');
      }, 1400);
    } catch (_) {
      setSubmitError('Erro ao conectar com o servidor. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !currentUser) return null;

  return (
    <div className="modal-overlay user-profile-overlay" onClick={onClose}>
      <div
        className="user-profile-modal"
        role="dialog"
        aria-labelledby="user-profile-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="user-profile-header-bar">
          <div className="user-profile-header-copy">
            <h3 id="user-profile-title" className="user-profile-title">Perfil</h3>
            <p className="user-profile-subtitle">Gerencie seus dados de acesso</p>
          </div>
          <button type="button" className="user-profile-close" onClick={onClose} aria-label="Fechar">
            <X size={14} strokeWidth={2} />
          </button>
        </div>

        <div className="user-profile-header-divider" aria-hidden />

        <div className="user-profile-body">
          <div className="user-profile-hero">
            <button
              type="button"
              className="user-profile-avatar"
              title="Alterar imagem"
              disabled={photoUploading}
              onClick={() => photoInputRef.current?.click()}
            >
              {photoPreviewUrl ? (
                <img src={photoPreviewUrl} alt="" className="user-profile-avatar-img" />
              ) : (
                <span className="user-profile-avatar-initials">{initials}</span>
              )}
            </button>
            <div className="user-profile-hero-main">
              <div className="user-profile-hero-name-row">
                <span className="user-profile-hero-name">{displayName || currentUser.username || 'Usuário'}</span>
                <span className="user-profile-role-badge">
                  <span className="user-profile-role-dot" aria-hidden />
                  {roleLabel}
                </span>
              </div>
              <div className="user-profile-hero-email">{email || '—'}</div>
              <div className="user-profile-hero-actions">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept={PROFILE_IMAGE_ACCEPT}
                  multiple={false}
                  className="user-profile-file-input"
                  onChange={handlePhotoSelect}
                />
                <button
                  type="button"
                  className="user-profile-choose-img-btn user-profile-tip-host"
                  data-tooltip="JPEG, PNG ou WebP · até 10 MB"
                  disabled={photoUploading}
                  onClick={() => photoInputRef.current?.click()}
                >
                  <Upload size={11} strokeWidth={2} aria-hidden />
                  {photoUploading ? 'Enviando...' : 'Alterar'}
                </button>
              </div>
            </div>
          </div>

          {successMessage && (
            <div className="user-profile-success" role="status">{successMessage}</div>
          )}
          {submitError && (
            <div className="user-profile-error" role="alert">{submitError}</div>
          )}

          <div className="user-profile-fields">
            <div className="user-profile-field-outline">
              <label htmlFor="user-profile-display-name">Nome e sobrenome</label>
              <input
                id="user-profile-display-name"
                type="text"
                value={displayName}
                maxLength={20}
                onChange={(e) => {
                  setDisplayName(e.target.value.slice(0, 20));
                  if (errors.displayName) setErrors((prev) => ({ ...prev, displayName: '' }));
                }}
              />
              {errors.displayName && <span className="user-profile-field-error">{errors.displayName}</span>}
            </div>

            <div className="user-profile-password-block">
              {!passwordSectionOpen ? (
                <div className="user-profile-field-outline">
                  <label htmlFor="user-profile-password-trigger">Senha</label>
                  <input
                    id="user-profile-password-trigger"
                    type="text"
                    readOnly
                    placeholder="Deixe em branco para manter a atual"
                    className="user-profile-password-trigger"
                    onFocus={() => setPasswordSectionOpen(true)}
                    onClick={() => setPasswordSectionOpen(true)}
                  />
                </div>
              ) : (
                <div className="user-profile-password-fields">
                  <div className="user-profile-field-outline">
                    <label htmlFor="user-profile-current-pwd">Senha atual</label>
                    <div className="user-profile-pwd-wrap">
                      <input
                        id="user-profile-current-pwd"
                        type={showPassword.current ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => {
                          setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }));
                          if (errors.currentPassword) setErrors((prev) => ({ ...prev, currentPassword: '' }));
                        }}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="user-profile-pwd-toggle"
                        onClick={() => setShowPassword((s) => ({ ...s, current: !s.current }))}
                        aria-label={showPassword.current ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showPassword.current ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {errors.currentPassword && <span className="user-profile-field-error">{errors.currentPassword}</span>}
                  </div>

                  <div className="user-profile-field-outline">
                    <label htmlFor="user-profile-new-pwd">Nova senha</label>
                    <div className="user-profile-pwd-wrap">
                      <input
                        id="user-profile-new-pwd"
                        type={showPassword.new ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPasswordForm((p) => ({ ...p, newPassword: v }));
                          if (passPolicy) {
                            setPasswordValidationRules(validatePasswordForPolicy(v, passPolicy).rules);
                          }
                          if (errors.newPassword) setErrors((prev) => ({ ...prev, newPassword: '' }));
                        }}
                        onFocus={() => setIsNewPasswordFocused(true)}
                        onBlur={() => setIsNewPasswordFocused(false)}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="user-profile-pwd-toggle"
                        onClick={() => setShowPassword((s) => ({ ...s, new: !s.new }))}
                        aria-label={showPassword.new ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showPassword.new ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {isNewPasswordFocused && passwordValidationRules.length > 0 && (
                      <ul className="password-policy-rules user-profile-policy-rules" aria-live="polite">
                        {passwordValidationRules.map((r, i) => (
                          <li key={i} className={r.ok ? 'password-rule-ok' : 'password-rule-fail'}>
                            <span className="password-rule-icon">{r.ok ? '✓' : '✗'}</span>
                            <span>{r.label}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {errors.newPassword && <span className="user-profile-field-error">{errors.newPassword}</span>}
                  </div>

                  <div className="user-profile-field-outline">
                    <label htmlFor="user-profile-confirm-pwd">Confirmar nova senha</label>
                    <div className="user-profile-pwd-wrap">
                      <input
                        id="user-profile-confirm-pwd"
                        type={showPassword.confirm ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => {
                          setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }));
                          if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: '' }));
                        }}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="user-profile-pwd-toggle"
                        onClick={() => setShowPassword((s) => ({ ...s, confirm: !s.confirm }))}
                        aria-label={showPassword.confirm ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showPassword.confirm ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <span className="user-profile-field-error">{errors.confirmPassword}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="user-profile-extra">
            {googleStatus && (googleStatus.enabled || googleStatus.linked) ? (
              <div className="user-profile-google-block">
                <span className="user-profile-google-mark" aria-hidden>
                  <IconBrandGoogle size={14} stroke={1.75} />
                </span>
                <div className="user-profile-google-copy">
                  <span className="user-profile-google-title">Conta Google</span>
                  <span className={`user-profile-google-desc${googleStatus.linked ? ' is-linked' : ''}`}>
                    <span className="user-profile-google-status-dot" aria-hidden />
                    {googleStatus.linked
                      ? `Vinculada${googleStatus.provider_email ? ` · ${googleStatus.provider_email}` : ''}`
                      : 'Não vinculada — necessária para entrar com Google'}
                  </span>
                </div>
                {googleStatus.linked ? (
                  <button
                    type="button"
                    className="user-profile-google-btn user-profile-google-btn--unlink"
                    disabled={googleBusy}
                    onClick={handleGoogleUnlink}
                  >
                    <Unlink size={13} strokeWidth={2} aria-hidden />
                    Desvincular
                  </button>
                ) : googleStatus.enabled && googleStatus.configured ? (
                  <button
                    type="button"
                    className="user-profile-google-btn"
                    disabled={googleBusy}
                    onClick={handleGoogleLink}
                  >
                    <Link2 size={13} strokeWidth={2} aria-hidden />
                    Vincular
                  </button>
                ) : null}
              </div>
            ) : null}

            <button
              type="button"
              className="user-profile-auth-history-btn"
              onClick={() => setShowAuthHistory(true)}
            >
              <History size={14} strokeWidth={2} aria-hidden />
              <span className="user-profile-auth-history-label">Histórico de acesso</span>
              <ChevronRight size={14} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </div>

        <div className="user-profile-footer">
          {isDirty ? (
            <button
              type="button"
              className="user-form-link-btn user-form-footer-btn"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </button>
          ) : null}
          <button
            type="button"
            className="user-form-profile-save-btn user-form-footer-btn"
            onClick={handleSave}
            disabled={isSaving || !isDirty}
          >
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>

      <AuthHistoryModal
        open={showAuthHistory}
        onClose={() => setShowAuthHistory(false)}
        mode="me"
      />
    </div>
  );
}

export default UserProfileModal;
